import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { TableDie } from '../presentation/raceDice.ts';
import { baseballDiceRecipe, createBaseballDiceCompositor, type BaseballDiceLayer } from '../presentation/baseballDiceFilm.ts';
import { d10FilmRecipe } from '../presentation/d10Film.ts';

/** The gray tabletop under the Baseball compositor. WebP; a JPEG fallback ships beside it. */
const TABLETOP_PLATE = '/baseball-dice-tabletop.webp';
const TABLETOP_PLATE_FALLBACK = '/baseball-dice-tabletop.jpg';
const PIPS: Record<number, number[]> = {1:[4],2:[0,8],3:[0,4,8],4:[0,2,6,8],5:[0,2,4,6,8],6:[0,2,3,5,6,8]};
export const roleName = (role: string) => role === 'metallic' ? 'Metallic' : role.charAt(0).toUpperCase() + role.slice(1);

/** Exact, accessible static faces when a movie cannot play. */
export function WoodenDie({die}: {die: TableDie}) {
  return <span className={`wooddie wooddie--${die.role}${die.sides === 10 ? ' wooddie--ten' : ''}`} role="img" aria-label={`${roleName(die.role)} d${die.sides}: ${die.face}`}>
    {die.sides === 6 ? Array.from({length:9},(_,i)=><i key={i} className={PIPS[die.face]?.includes(i) ? 'woodpip is-inked' : 'woodpip'} />) : <b>{die.face}</b>}
  </span>;
}

/**
 * A throw no animated renderer covers — four or more d6, or mixed sides. Its
 * exact recorded faces, shown still. (The 157 MB whole-scene footage that used
 * to sit here was only ever selected for throws the two renderers below already
 * draw; see `jointDice.ts`.) A rolling throw settles at once: there is nothing
 * to wait for, and the reveal sequence must not stall on it.
 */
function StaticDice({dice,rolling=false,onSettled}: {dice: readonly TableDie[]; rolling?: boolean; onSettled?:()=>void}) {
  const callback=useRef(onSettled);callback.current=onSettled;
  const signature=JSON.stringify(dice);
  useEffect(()=>{if(rolling&&dice.length)callback.current?.();},[signature,rolling]); // eslint-disable-line react-hooks/exhaustive-deps
  return <div className="woodtray" aria-label="Dice on the table">
    {dice.length>0&&<div className="woodtray__fallback" style={{'--dice-count':dice.length} as CSSProperties}>
      {dice.map((die,index)=><WoodenDie key={index} die={die}/>)}
    </div>}
    {dice.length > 0 && <div className="woodtray__values" aria-label="Settled dice">{dice.map((die,index)=><span key={index}>{roleName(die.role)} · {die.face}</span>)}</div>}
  </div>;
}

/**
 * A throw drawn as film layers over the shared tabletop: Baseball Slop's layered
 * entrance, collision depth, timing and exact-face fallback. The d6 recipe
 * (`baseballDiceRecipe`) and the d10 recipe (`d10FilmRecipe`, D-EZ-106) both feed
 * it, so the two kinds of die share one compositor, plate, grade and shadow.
 * Reduced motion, or a throw that is already settled, draws the exact settled
 * stills at once; a film that cannot load falls back to the exact faces.
 */
function FilmDice({dice,layers,rolling,sound,readings,label,onSettled}: {
  dice: readonly TableDie[]; layers: readonly BaseballDiceLayer[]; rolling:boolean; sound:boolean;
  readings: readonly string[]; label: string; onSettled?:()=>void;
}) {
  const canvas=useRef<HTMLCanvasElement>(null), callback=useRef(onSettled), audioTracks=useRef<HTMLAudioElement[]>([]);
  callback.current=onSettled;
  const [failed,setFailed]=useState(false), [revealed,setRevealed]=useState(!rolling);
  useEffect(()=>{audioTracks.current.forEach(audio=>{audio.muted=!sound;});},[sound]);
  useEffect(()=>{
    let cancelled=false,done=false,raf=0;let renderer:ReturnType<typeof createBaseballDiceCompositor>|undefined;
    const videos:HTMLVideoElement[]=[],audio:HTMLAudioElement[]=[];audioTracks.current=audio;setFailed(false);setRevealed(!rolling);
    const finish=(error=false)=>{if(cancelled||done)return;done=true;cancelAnimationFrame(raf);videos.forEach(v=>v.pause());audio.forEach(a=>a.pause());if(error)setFailed(true);setRevealed(true);if(rolling)callback.current?.();};
    const timeout=window.setTimeout(()=>finish(true),10000);
    const image=(src:string)=>new Promise<HTMLImageElement>((resolve,reject)=>{const item=new Image();item.onload=()=>resolve(item);item.onerror=reject;item.src=src;});
    void (async()=>{
      const plate=await image(TABLETOP_PLATE).catch(()=>image(TABLETOP_PLATE_FALLBACK));if(cancelled||done||!canvas.current)return;
      renderer=createBaseballDiceCompositor(canvas.current,layers,plate);
      if(filmPlayback(rolling,!!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)==='still'){const stills=await Promise.all(layers.map(layer=>image(layer.still)));if(cancelled||done)return;renderer.draw(stills,27);clearTimeout(timeout);finish();return;}
      await Promise.all(layers.map(layer=>new Promise<void>((resolve,reject)=>{const video=document.createElement('video');video.muted=true;video.playsInline=true;video.preload='auto';video.oncanplay=()=>resolve();video.onerror=()=>reject(new Error('Dice film unavailable'));video.src=layer.movie;video.load();videos.push(video);})));if(cancelled||done)return;
      await Promise.all(videos.map(video=>video.play()));if(cancelled||done)return;
      if(sound)layers.forEach(layer=>{const track=new Audio(layer.sound);track.volume=.25;audio.push(track);void track.play().catch(()=>{});});
      const draw=()=>{if(cancelled||done)return;try{renderer?.draw(videos,Math.min(63,Math.floor((videos[0]?.currentTime??0)*24)));if(videos.every(video=>video.ended)){clearTimeout(timeout);finish();return;}}catch{finish(true);return;}raf=requestAnimationFrame(draw);};draw();
    })().catch(()=>finish(true));
    return()=>{cancelled=true;clearTimeout(timeout);cancelAnimationFrame(raf);videos.forEach(video=>{video.pause();video.removeAttribute('src');video.load();});audio.forEach(track=>track.pause());renderer?.dispose();};
  },[layers,rolling,sound]);
  return <div className="woodtray woodtray--baseball" aria-label={rolling&&!revealed?`${label} rolling on the table`:`${label} settled on the table`}>
    <canvas className="woodtray__film" ref={canvas} aria-hidden="true" />
    {failed&&<div className="woodtray__fallback">{dice.map((die,index)=><WoodenDie key={index} die={die}/>)}</div>}
    {revealed&&<div className="woodtray__values" aria-label="Settled dice">{readings.map((reading,index)=><span key={index}>{reading}</span>)}</div>}
  </div>;
}

/**
 * How a film throw starts: a throw that is already settled, or any throw when
 * the reader asks for reduced motion, draws the exact settled stills (the
 * recorded faces) at once; otherwise the films play.
 */
export function filmPlayback(rolling: boolean, reducedMotion: boolean): 'film' | 'still' {
  return rolling && !reducedMotion ? 'film' : 'still';
}

/** What the settled tray reads out: role and face for d6s; face (and total) for d10s. */
export function filmReadings(dice: readonly TableDie[]): string[] {
  if (dice.length && dice.every(die => die.sides === 10)) {
    const faces = dice.map(die => `Ten-sided · ${die.face}`);
    return dice.length > 1 ? [...faces, `Total · ${dice.reduce((sum, die) => sum + die.face, 0)}`] : faces;
  }
  return dice.map(die => `${roleName(die.role)} · ${die.face}`);
}

function BaseballStyleDice({dice,rolling,variation,sound,onSettled}: {
  dice: readonly TableDie[]; rolling:boolean; variation:number; sound:boolean; onSettled?:()=>void;
}) {
  const layers=useMemo(()=>baseballDiceRecipe(dice,variation)!,[dice,variation]);
  return <FilmDice dice={dice} layers={layers} rolling={rolling} sound={sound} readings={filmReadings(dice)} label="Dice" onSettled={onSettled}/>;
}

/** D-EZ-106: the ivory ten-sided die, rolled on the same table as the d6s. */
function TenSidedFilmDice({dice,rolling,variation,sound,onSettled}: {
  dice: readonly TableDie[]; rolling:boolean; variation:number; sound:boolean; onSettled?:()=>void;
}) {
  const layers=useMemo(()=>d10FilmRecipe(dice,variation)!,[dice,variation]);
  return <FilmDice dice={dice} layers={layers} rolling={rolling} sound={sound} readings={filmReadings(dice)} label={dice.length>1?'Ten-sided dice':'Ten-sided die'} onSettled={onSettled}/>;
}

/**
 * WHICH RENDERER DRAWS A THROW. Pure, so the test can walk every notation the
 * engine emits through it: 'baseball' for one to three d6, 'd10' for one or two
 * d10 (the ivory d10 films, D-EZ-106), 'static' for anything else (exact faces,
 * no film).
 */
export function diceRenderer(dice: readonly TableDie[], variation = 0): 'baseball' | 'd10' | 'static' {
  if (baseballDiceRecipe(dice, variation)) return 'baseball';
  if (d10FilmRecipe(dice, variation)) return 'd10';
  return 'static';
}

export default function WoodenDice(props:{dice:readonly TableDie[];rolling?:boolean;variation?:number;sound?:boolean;onSettled?:()=>void}) {
  const rolling=props.rolling??false,variation=props.variation??0,sound=props.sound??false;
  const renderer=diceRenderer(props.dice,variation);
  if(renderer==='baseball')return <BaseballStyleDice dice={props.dice} rolling={rolling} variation={variation} sound={sound} onSettled={props.onSettled}/>;
  if(renderer==='d10')return <TenSidedFilmDice dice={props.dice} rolling={rolling} variation={variation} sound={sound} onSettled={props.onSettled}/>;
  return <StaticDice dice={props.dice} rolling={rolling} onSettled={props.onSettled}/>;
}
